import { supabase } from '../lib/supabase';

export const savedMealsService = {
  /**
   * Get all saved meal collections for a user
   */
  async getCollections(userId) {
    const { data, error } = await supabase
      ?.from('saved_meal_collections')
      ?.select(`
        *,
        saved_meal_items (*)
      `)
      ?.eq('user_id', userId)
      ?.order('use_count', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Create a new saved meal collection from current meal foods
   */
  async createCollection(userId, { name, mealType, description, foods }) {
    const totalCalories = foods?.reduce((sum, f) => sum + (f?.calories || 0), 0);
    const totalProtein = foods?.reduce((sum, f) => sum + parseFloat(f?.protein || 0), 0);
    const totalCarbs = foods?.reduce((sum, f) => sum + parseFloat(f?.carbs || 0), 0);
    const totalFat = foods?.reduce((sum, f) => sum + parseFloat(f?.fat || f?.fats || 0), 0);

    const { data: collection, error: collectionError } = await supabase
      ?.from('saved_meal_collections')
      ?.insert({
        user_id: userId,
        name,
        meal_type: mealType || 'snack',
        description: description || '',
        total_calories: Math.round(totalCalories),
        total_protein: Math.round(totalProtein * 10) / 10,
        total_carbs: Math.round(totalCarbs * 10) / 10,
        total_fat: Math.round(totalFat * 10) / 10,
        use_count: 0
      })
      ?.select()
      ?.single();

    if (collectionError) throw collectionError;

    if (foods?.length > 0) {
      const items = foods?.map(food => ({
        collection_id: collection?.id,
        food_name: food?.food_name || food?.name || 'Alimento',
        calories: food?.calories || 0,
        protein: parseFloat(food?.protein || 0),
        carbs: parseFloat(food?.carbs || 0),
        fat: parseFloat(food?.fat || food?.fats || 0),
        fiber: parseFloat(food?.fiber || 0),
        serving_size: food?.serving_size || `${food?.portionMultiplier || 1}x`,
        quantity_grams: food?.quantity || 100
      }));

      const { error: itemsError } = await supabase
        ?.from('saved_meal_items')
        ?.insert(items);

      if (itemsError) throw itemsError;
    }

    return collection;
  },

  /**
   * Delete a saved meal collection
   */
  async deleteCollection(collectionId) {
    const { error } = await supabase
      ?.from('saved_meal_collections')
      ?.delete()
      ?.eq('id', collectionId);

    if (error) throw error;
  },

  /**
   * Increment use_count when a collection is quick-added
   */
  async incrementUseCount(collectionId) {
    const { error } = await supabase
      ?.rpc('increment_saved_meal_use_count', { collection_uuid: collectionId })
      ?.catch(() => {
        // Fallback if RPC not available: manual increment
        return supabase
          ?.from('saved_meal_collections')
          ?.select('use_count')
          ?.eq('id', collectionId)
          ?.single()
          ?.then(({ data }) => {
            return supabase
              ?.from('saved_meal_collections')
              ?.update({ use_count: (data?.use_count || 0) + 1 })
              ?.eq('id', collectionId);
          });
      });

    // Non-critical: don't throw on error
    if (error) console.warn('Could not increment use count:', error);
  }
};
